import { Link, useParams } from 'react-router-dom'
import {
  AlertTriangle,
  CheckCircle,
  XCircle,
  MoreVertical,
  Trash2,
  Clock,
  Settings,
  Radio,
  Loader2,
} from 'lucide-react'
import { useState, useRef, useEffect } from 'react'
import type { AppWithStats } from '../types'

interface AppRowProps {
  app: AppWithStats
  onDelete: (id: string) => Promise<void>
  onSettings: (app: AppWithStats) => void
  onPing?: (id: string) => Promise<void>
}

export default function AppRow({ app, onDelete, onSettings, onPing }: AppRowProps) {
  const { teamId } = useParams<{ teamId: string }>()
  const [menuOpen, setMenuOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isPinging, setIsPinging] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleDelete = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (confirm('Are you sure you want to delete this app? All data will be lost.')) {
      setIsDeleting(true)
      try {
        await onDelete(app.id)
      } finally {
        setIsDeleting(false)
      }
    }
    setMenuOpen(false)
  }

  const handlePing = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!onPing || !app.health_url) return
    setIsPinging(true)
    setMenuOpen(false)
    try {
      await onPing(app.id)
    } finally {
      setIsPinging(false)
    }
  }

  // Derive ping status from last_ping object
  const lastPingStatus = app.last_ping?.is_success ?? null

  const getStatusIcon = () => {
    if (lastPingStatus === null) {
      return <Clock className="w-4 h-4 text-slate-500" />
    }
    if (lastPingStatus === true) {
      return <CheckCircle className="w-4 h-4 text-green-600" />
    }
    return <XCircle className="w-4 h-4 text-red-600" />
  }

  const getRowStyle = () => {
    if (app.unresolved_errors > 0 || lastPingStatus === false) {
      return 'border-l-4 border-l-red-500'
    }
    if (app.error_count_24h > 0) {
      return 'border-l-4 border-l-yellow-500'
    }
    return 'border-l-4 border-l-transparent'
  }

  return (
    <Link
      to={`/t/${teamId}/apps/${app.id}`}
      className={`flex items-center gap-4 px-4 py-3 bg-white hover:bg-slate-50 border-b border-slate-100 transition-colors ${getRowStyle()}`}
    >
      {/* Status icon */}
      <div className="flex-shrink-0">{getStatusIcon()}</div>

      {/* App name */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-slate-900 truncate">{app.name}</span>
          {(app.unresolved_errors > 0 || lastPingStatus === false) && (
            <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
          )}
        </div>
        {app.description && <p className="text-sm text-slate-500 truncate">{app.description}</p>}
      </div>

      {/* Stats */}
      <div className="flex items-center gap-6 text-sm flex-shrink-0">
        <div className="w-20 text-center">
          <span className={app.error_count_24h > 0 ? 'text-red-600 font-medium' : 'text-slate-600'}>
            {app.error_count_24h}
          </span>
          <span className="text-slate-400 ml-1">24h</span>
        </div>
        <div className="w-20 text-center">
          <span
            className={app.unresolved_errors > 0 ? 'text-yellow-600 font-medium' : 'text-slate-600'}
          >
            {app.unresolved_errors}
          </span>
          <span className="text-slate-400 ml-1">open</span>
        </div>
        <div className="w-16 text-center text-slate-600">
          {app.uptime_24h !== null ? `${app.uptime_24h.toFixed(1)}%` : '-'}
        </div>
      </div>

      {/* Menu button */}
      <div className="relative flex-shrink-0" ref={menuRef}>
        <button
          onClick={e => {
            e.preventDefault()
            e.stopPropagation()
            setMenuOpen(!menuOpen)
          }}
          className="p-1 hover:bg-slate-200 rounded transition-colors"
        >
          <MoreVertical className="w-4 h-4 text-slate-400" />
        </button>
        {menuOpen && (
          <div className="absolute right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-10 min-w-[120px]">
            {onPing && app.health_url && (
              <button
                onClick={handlePing}
                disabled={isPinging}
                className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 flex items-center gap-2 disabled:opacity-50"
              >
                {isPinging ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Radio className="w-4 h-4" />
                )}
                {isPinging ? 'Pinging...' : 'Ping Now'}
              </button>
            )}
            <button
              onClick={e => {
                e.preventDefault()
                e.stopPropagation()
                setMenuOpen(false)
                onSettings(app)
              }}
              className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 flex items-center gap-2"
            >
              <Settings className="w-4 h-4" />
              Settings
            </button>
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-slate-100 flex items-center gap-2 disabled:opacity-50"
            >
              <Trash2 className="w-4 h-4" />
              {isDeleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        )}
      </div>
    </Link>
  )
}
